import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';

export interface ButtonStyle {
  variant?: ButtonVariant;
  /** L 52 (default) or M 44. */
  size?: 'l' | 'm';
  /** Full width, inside sheets and action bars. */
  block?: boolean;
  className?: string;
}

/** Class names for a Button look, for links styled as buttons (`<Link className={…}>`). */
export function buttonClass({ variant = 'secondary', size = 'l', block, className }: ButtonStyle) {
  return [
    'button',
    `button--${variant}`,
    size === 'm' && 'button--m',
    block && 'button--block',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * DESIGN.md §10 Button. At most one primary per view; destructive actions confirm in a sheet.
 * `loading` swaps the icon for a spinner, keeps the label and blocks clicks.
 */
export function Button({
  variant,
  size,
  block,
  icon,
  loading = false,
  className,
  children,
  type = 'button',
  disabled,
  ...rest
}: ButtonStyle &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
    icon?: IconName;
    loading?: boolean;
    children: ReactNode;
  }) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, size, block, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon && <Icon name={icon} />}
      {children}
    </button>
  );
}
